import { useState } from "react"
import { Box, IconButton, Tooltip } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
    faComment,
    faEllipsisV,
    faList,
    faPenToSquare,
    faPrint,
    faReceipt,
    faTrash,
} from "@fortawesome/pro-light-svg-icons"
import { alpha } from "@mui/material/styles"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../../../localization/useDateTime"
import { OrderChatIcon } from "../../../../../../../shared/components/orderChat/OrderChatIcon"
import { MeetingServiceOrder, MeetingServiceOrdersView } from "../../meetingServiceTypes"
import { MeetingServiceOrdersDependencies } from "../../components/OrderDeliveryList"
import { isMeetingInProgress } from "../helpers"
// Hidden for now (Task 260) — restore the green "delivered" dot together with `isDeliveredStatus`:
// import { isDeliveredStatus, isMeetingInProgress } from "../helpers"
import { useProductionOrderEdit } from "../useProductionOrderEdit"
import { ProductionCardMenu, ProductionCardMenuItem } from "./ProductionCardMenu"
import { ProductionOrderCardHeader } from "./ProductionOrderCardHeader"
import { ProductionOrderItemsSection } from "./ProductionOrderItemsSection"
import { ProductionOrderCardOverlays } from "./ProductionOrderCardOverlays"

const useStyles = makeStyles()((theme) => ({
    card: {
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
        padding: 0,
        borderRadius: ".625rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        background: theme.palette.background.paper,
        overflow: "hidden",
    },
    content: {
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        flexGrow: 1,
        minWidth: 0,
        background: theme.palette.background.paper,
    },
    rail: {
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        width: "5rem",
        borderLeft: `1px solid ${theme.palette.tailwind.slate[200]}`,
        background: theme.palette.background.paper,
    },
    railTop: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        boxSizing: "border-box",
        height: "4.5rem",
        padding: ".5rem 1rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    railDot: {
        width: "1rem",
        height: "1rem",
        borderRadius: "50%",
        background: theme.palette.tailwind.red[600],
        boxShadow: `0 0 0 .5rem ${alpha(theme.palette.tailwind.red[600], 0.25)}`,
    },
    railDotDelivered: {
        background: theme.palette.tailwind.green[600],
        boxShadow: `0 0 0 .5rem ${alpha(theme.palette.tailwind.green[600], 0.25)}`,
    },
    railButtons: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        alignItems: "center",
        flexGrow: 1,
        padding: "1rem",
        gap: "1.5rem",
    },
    railButton: {
        width: "3rem",
        height: "3rem",
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        borderRadius: ".75rem",
        color: theme.palette.tailwind.slate[700],
        "& svg": {
            fontSize: "1.125rem",
        },
    },
    menuItemDelete: {
        color: theme.palette.error.main,
    },
}))

type Props = Readonly<{
    order: MeetingServiceOrder
    onEdit: (order: MeetingServiceOrder) => void
    onDelete: (order: MeetingServiceOrder) => void
    onSaved: () => void
    isSelected: boolean
    onToggleSelect: () => void
    meetingServiceDependencies: MeetingServiceOrdersDependencies
}>

export function ProductionOrderCard({
    order,
    onEdit,
    onDelete,
    onSaved,
    isSelected,
    onToggleSelect,
    meetingServiceDependencies,
}: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()

    // Rail dot: red while the meeting is in progress, hidden otherwise.
    // Hidden for now (Task 260) — the green "delivered" dot; restore with `cx` from useStyles():
    // const isDelivered = isDeliveredStatus(order.deliveryDetails.statusDetails.status)
    // const showDot = isDelivered || isMeetingInProgress(order, dateTimeFactory, dateTimeFactory.now())
    const showDot = isMeetingInProgress(order, dateTimeFactory, dateTimeFactory.now())

    const [showReceipt, setShowReceipt] = useState(false)
    const [showChat, setShowChat] = useState(false)
    const [showPackingSlip, setShowPackingSlip] = useState(false)
    const [printMenuAnchor, setPrintMenuAnchor] = useState<HTMLElement | null>(null)
    const [moreMenuAnchor, setMoreMenuAnchor] = useState<HTMLElement | null>(null)

    const edit = useProductionOrderEdit({
        order,
        onRequestDelete: () => onDelete(order),
        // The saved-dialog lives on the page (not the card) so it survives this card unmounting when the
        // edited order drops out of the filtered list after the post-save refetch.
        onSaved,
    })

    const printMenuItems: ProductionCardMenuItem[] = [
        {
            id: "meeting-service-table-production-slip-button",
            icon: faList,
            fallback: "Packing slip",
            onClick: () => setShowPackingSlip(true),
        },
        {
            id: "user-home-canteen-upcoming-orders-receipt-item",
            icon: faReceipt,
            fallback: "Receipt",
            onClick: () => setShowReceipt(true),
        },
    ]

    const moreMenuItems: ProductionCardMenuItem[] = [
        {
            id: "meeting-service-production-view-card-full-edit",
            icon: faPenToSquare,
            fallback: "Full edit",
            onClick: () => onEdit(order),
        },
        {
            id: "order-chat-menu-item",
            icon: faComment,
            fallback: "Order message",
            onClick: () => setShowChat(true),
        },
        {
            id: "meeting-service-production-view-card-delete-button",
            icon: faTrash,
            fallback: "Delete",
            onClick: () => onDelete(order),
            isDelete: true,
        },
    ]

    return (
        <>
            <Box className={classes.card}>
                <Box className={classes.content}>
                    <ProductionOrderCardHeader
                        order={order}
                        isSelected={isSelected}
                        onToggleSelect={onToggleSelect}
                        view={meetingServiceDependencies.view ?? MeetingServiceOrdersView.PRODUCTION}
                        meetingServiceDependencies={meetingServiceDependencies}
                    />

                    <ProductionOrderItemsSection order={order} edit={edit} onStartEdit={edit.startEdit} />
                </Box>

                <Box className={classes.rail}>
                    <Box className={classes.railTop}>
                        {/* Hidden for now (Task 260): green variant → cx(classes.railDot, isDelivered && classes.railDotDelivered) */}
                        {showDot && <Box className={classes.railDot} />}
                    </Box>
                    <Box className={classes.railButtons}>
                        <Tooltip arrow title={l10n.getString("meeting-service-production-view-print-menu")}>
                            <IconButton
                                size="small"
                                className={classes.railButton}
                                aria-label={l10n.getString("meeting-service-production-view-print-menu")}
                                onClick={(event) => setPrintMenuAnchor(event.currentTarget)}
                            >
                                <FontAwesomeIcon icon={faPrint} />
                            </IconButton>
                        </Tooltip>
                        <ProductionCardMenu
                            anchorEl={printMenuAnchor}
                            items={printMenuItems}
                            onClose={() => setPrintMenuAnchor(null)}
                        />

                        <OrderChatIcon
                            messages={order.messages}
                            onClick={() => setShowChat(true)}
                            className={classes.railButton}
                        />

                        <LocalizedStrict
                            id="meeting-service-production-view-card-menu-button"
                            attrs={{ "aria-label": true }}
                        >
                            <IconButton
                                size="small"
                                className={classes.railButton}
                                aria-label="More"
                                onClick={(event) => setMoreMenuAnchor(event.currentTarget)}
                            >
                                <FontAwesomeIcon icon={faEllipsisV} />
                            </IconButton>
                        </LocalizedStrict>
                        <ProductionCardMenu
                            anchorEl={moreMenuAnchor}
                            items={moreMenuItems}
                            deleteClassName={classes.menuItemDelete}
                            onClose={() => setMoreMenuAnchor(null)}
                        />
                    </Box>
                </Box>
            </Box>

            <ProductionOrderCardOverlays
                order={order}
                showReceipt={showReceipt}
                onCloseReceipt={() => setShowReceipt(false)}
                showChat={showChat}
                onCloseChat={() => setShowChat(false)}
                showPackingSlip={showPackingSlip}
                onClosePackingSlip={() => setShowPackingSlip(false)}
            />
        </>
    )
}
